import { memo, useEffect, useLayoutEffect, useState } from "react";
import { FormProvider, UseFormSetValue } from "react-hook-form";
import * as yup from "yup";

import {
  useCheckoutPage,
  useDebounce,
  useForceUpdate,
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQueryWithParams,
} from "../../../hooks";
import {
  IInitPaymentLinkGatewayTransactionRequest,
  IInitPaymentResponse,
  IViewHostedPaymentLink,
  IViewMerchantEnabledCheckoutGateway,
  PagedMeta,
  IPageAndSearchRequest,
  IHookForm,
  ISwAppTransactionsApi,
  IResolveBankAccountResponse,
} from "../../../interfaces-and-types";
import { globalHandlerUtils } from "../../../utils";

import { View } from "./View";
import { switchappPaymentChannel } from "../../../enums";

interface IGatewayFormProps {
  isPreviewMode: boolean;
  isActive: boolean;
  isLineItemQtyInputTempDisabled: boolean;

  isSuccessHostedPaymentLink: boolean;
  hostedPaymentLink: IViewHostedPaymentLink;
  showSuccessPage: () => void;
  openInIframe: (url: string) => void;
}

export interface IGatewayFormViewProps
  extends IHookForm<IInitPaymentLinkGatewayTransactionRequest> {
  isActive: boolean;
  searchLineItemsText: string | null;
  isLineItemQtyInputTempDisabled: boolean;
  saleTotal: number;

  hostedPaymentLink: IViewHostedPaymentLink;

  isLoadingMerchantGateways: boolean;
  isSuccessMerchantGateways: boolean;
  merchantEnabledGateways: IViewMerchantEnabledCheckoutGateway[];
  searchGatewaysText: string | null;
  setSearchGatewaysText: (value: string | null) => void;

  isVirtualAccountPayment?: boolean; // is_virtual_account_payment
  gatewayReservedVirtualAccounts?: IResolveBankAccountResponse[]; // gateway_reserved_virtual_accounts
  selectedPaymentChannel: switchappPaymentChannel;
  isVirtualBankTransferConfirmationPending: boolean;
  setSelectedPaymentChannel: (paymentChannel: switchappPaymentChannel) => void;
  closeVirtualBankAccountsView: () => void;
  setBankTransferCompleted: () => void;
  showSuccessPage: () => void;

  selectedGatewayCode: string;
  setGatewayCode: (gateway_code: string) => void;

  setValue: UseFormSetValue<IInitPaymentLinkGatewayTransactionRequest>;
  formValues: IInitPaymentLinkGatewayTransactionRequest;
}

export const GatewayForm = memo((props: IGatewayFormProps) => {
  const {
    isPreviewMode,
    isActive,
    isLineItemQtyInputTempDisabled,
    isSuccessHostedPaymentLink,
    hostedPaymentLink,
    showSuccessPage,
    openInIframe,
  } = props;

  const [selectedPaymentChannel, setSelectedPaymentChannel] =
    useState<switchappPaymentChannel>(switchappPaymentChannel.gateway);

  const {
    isInitialized,
    lockCheckoutItems,
    country,
    setCountry,
    customer,
    setCustomer,
    setSaleTotal,
    setSaleCurrency,
    searchLineItemsText,
    line_items,
    setLineItems,
  } = useCheckoutPage();

  const ResetForm = () => {
    reset({
      country,
      tx_ref: hostedPaymentLink.tx_ref,
      sw_app_tx_ref: hostedPaymentLink.sw_app_tx_ref, // TODO: should be handled on the BACKEND; remove after publicApiKey implementation
      sw_app_payment_link_url: hostedPaymentLink.link_url,
      line_items: isInitialized
        ? line_items
        : hostedPaymentLink.line_items.map((item) => ({
            product: item.product,
            qty: item.qty,
          })),
      customer: isInitialized ? customer : formValues.customer,
    });
  };

  useLayoutEffect(() => {
    if (isSuccessHostedPaymentLink && hostedPaymentLink) {
      // console.log("resetting checkout form...");
      ResetForm();
    }
  }, [isSuccessHostedPaymentLink, hostedPaymentLink]);

  useLayoutEffect(() => {
    if (isPreviewMode && hostedPaymentLink) {
      // console.log("resetting preview form...");
      ResetForm();
    }
  }, [isPreviewMode, isInitialized, hostedPaymentLink]);

  // customer has selected bank transfer as method of payment
  const [isVirtualAccountPayment, setVirtualAccountPayment] = useState(false);

  // customer has clicked "I have completed the transfer"
  const [
    isVirtualBankTransferConfirmationPending,
    setVirtualBankTransferConfirmationPending,
  ] = useState(false);

  const apiEndpointToCall: keyof ISwAppTransactionsApi =
    selectedPaymentChannel ===
    switchappPaymentChannel.switchAppVirtualBankTransfer
      ? hostedPaymentLink.is_top_up_payment_link
        ? "initTopUpVirtualBankAccTransaction"
        : "initVirtualBankAccTransaction"
      : hostedPaymentLink.is_top_up_payment_link
      ? "initTopUpGatewayTransaction"
      : "initPaymentLinkGatewayTransaction";

  const {
    // isLoading: isLoadingInitTxForGateway,
    // isError: isErrorInitTxForGateway,
    isSuccess: isSuccessInitTxForGateway,
    data: initTxForGatewayApiResponse,
    mutate: initTxForGateway,
  } = useMerchantApiMutation<
    IInitPaymentLinkGatewayTransactionRequest,
    IInitPaymentResponse
  >(apiEndpointToCall).usePostMutation;

  useEffect(() => {
    if (isSuccessInitTxForGateway) {
      if (!initTxForGatewayApiResponse.data.is_virtual_account_payment) {
        initTxForGatewayApiResponse &&
          openInIframe(initTxForGatewayApiResponse.data.gateway_payment_link);
      } else {
        if (!isVirtualAccountPayment) setVirtualAccountPayment(true);
      }
    }
  }, [isSuccessInitTxForGateway]);

  const initialFormValues: IInitPaymentLinkGatewayTransactionRequest = {
    country,
    // refs
    sw_app_tx_ref: null,
    tx_ref: null,

    // payment link details
    sw_app_payment_link_url: "",

    // sale
    line_items: [],

    // gateway details
    gateway_code: "",

    customer: {
      full_name: "",
      email: "",
      phone_number: "",
    },
  };

  // TODO: add payment link requirements to the form eg customer address, tax, etc
  const gatewayFormSchema: yup.SchemaOf<IInitPaymentLinkGatewayTransactionRequest> =
    yup
      .object({
        // gateway_code: yup.string().when("sw_app_payment_link_url", {
        //   is: (_: string) => paymentMethodName === "gateway",
        //   then: yup.string().required("Please select a gateway"),
        // }),
        country: yup.string().required("Country is required"),
        tx_ref: yup.string().required("tx_ref is required"),
        sw_app_tx_ref: yup.string().required("sw_app_tx_ref is required"),
        gateway_code: yup.string().required("Please select a payment provider"),
        sw_app_payment_link_url: yup
          .string()
          .required("Payment link Url is required"),

        line_items: yup.array().of(
          yup.object().shape({
            product: yup.string().required("product is required"),
            qty: yup
              .number()
              // .min(min_qty, "Min quantity must be greater than or equal to min")
              // .max(max_qty, "Max quantity must be less than or equal to {0}")
              .required("Qty is required"),
          })
        ),

        customer: yup
          .object({
            // email: yup.string().email().required("Customer Email is required"),
            email: yup
              .string()
              .email()
              .nullable()
              .optional()
              .when("sw_app_payment_link_url", {
                is: (_: string) =>
                  !hostedPaymentLink.is_temp_payment_link &&
                  !hostedPaymentLink.is_top_up_payment_link,
                then: yup.string().required("Customer Email is required"),
              }),
            full_name: yup
              .string()
              .nullable()
              .optional()
              .when("sw_app_payment_link_url", {
                is: (_: string) => hostedPaymentLink.require_customer_full_name,
                then: yup.string().required("Customer full name is required"),
              }),
            address: yup
              .string()
              .nullable()
              .optional()
              .when("sw_app_payment_link_url", {
                is: (_: string) => hostedPaymentLink.require_customer_address,
                then: yup.string().required("Customer full name is required"),
              }),
            phone_number: yup
              .string()
              .nullable()
              .optional()
              .when("sw_app_payment_link_url", {
                is: (_: string) =>
                  hostedPaymentLink.require_customer_phone_number,
                then: yup
                  .string()
                  .required("Customer phone number is required"),
              }),
          })
          .required("Customer details are required"),
      })
      .required();

  const handleApiSubmit = (data: IInitPaymentLinkGatewayTransactionRequest) => {
    lockCheckoutItems(); // no more qty editing
    !isPreviewMode && initTxForGateway(data);
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const {
    formContextMethods,
    control,
    onFormSubmit,
    errors,
    register,
    reset,
    watch,
    setValue,
  } = useHookForm({
    initialFormValues: initialFormValues,
    validationSchema: gatewayFormSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();

  const [searchGatewaysText, setSearchGatewaysText] = useState<string | null>(
    null
  );

  const isSearchTextDebounceElapsed = useDebounce(1200, searchGatewaysText);
  const isSkeletonJitterDebounceElapsed = useDebounce(500, formValues.country);

  const {
    isLoading: isLoadingMerchantGateways,
    isSuccess: isSuccessMerchantGateways,
    data: merchantEnabledGatewaysPage,
    // meta: productsMeta,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest & { country: string },
    IViewMerchantEnabledCheckoutGateway[],
    PagedMeta
  >(
    [
      "getMerchantEnabledCheckoutGateways",
      {
        page: 1,
        page_size: 20,
        search: searchGatewaysText,
        country: formValues.country,
      },
    ],
    {
      enabled: isSearchTextDebounceElapsed,
      keepPreviousData: isSkeletonJitterDebounceElapsed,
    }
  );

  const saleTotal = formValues.line_items.reduce((acc, item) => {
    const plLineItem = hostedPaymentLink.line_items.find(
      (plItem) => plItem.product === item.product
    );

    return acc + item.qty * plLineItem!.price;
  }, 0);

  const forceUpdate = useForceUpdate(); // TODO: find and fix bug that causes need for re-render

  // TODO: remove crude implementation:
  // reason for implemenation: closure bug? that restores previous values on clicking submit
  useLayoutEffect(
    () => forceUpdate(),
    [
      isActive,
      formValues.country,

      formValues.customer.email,
      formValues.customer.full_name,
      formValues.customer.address,
      formValues.customer.phone_number,

      formValues.gateway_code,

      formValues.tx_ref,
      formValues.sw_app_tx_ref,
      formValues.sw_app_payment_link_url,
    ]
  );

  // update sale lineitems
  useLayoutEffect(() => {
    setCountry(formValues.country);
    setLineItems(formValues.line_items);
    setCustomer(formValues.customer);
    setSaleTotal(saleTotal);
    setSaleCurrency(hostedPaymentLink.currency);
  }, [formValues]);

  const closeVirtualBankAccountsView = () => {
    if (
      window.confirm(
        "Are you sure you want to close this view? Your payment has not been confirmed!!!"
      )
    ) {
      setVirtualAccountPayment(false);
      setVirtualBankTransferConfirmationPending(false);
    }
  };

  return (
    <FormProvider {...formContextMethods}>
      <View
        isActive={isActive}
        searchLineItemsText={searchLineItemsText}
        // loading & searching gateways
        isLoadingMerchantGateways={isLoadingMerchantGateways}
        isSuccessMerchantGateways={isSuccessMerchantGateways}
        merchantEnabledGateways={merchantEnabledGatewaysPage || []}
        searchGatewaysText={searchGatewaysText}
        setSearchGatewaysText={setSearchGatewaysText}
        // qty management
        isLineItemQtyInputTempDisabled={isLineItemQtyInputTempDisabled}
        setValue={setValue}
        formValues={formValues}
        saleTotal={saleTotal}
        hostedPaymentLink={hostedPaymentLink}
        // gateway form values
        selectedGatewayCode={formValues.gateway_code || ""}
        setGatewayCode={(gateway_code) =>
          setValue("gateway_code", gateway_code)
        }
        showSuccessPage={showSuccessPage}
        selectedPaymentChannel={selectedPaymentChannel}
        setSelectedPaymentChannel={setSelectedPaymentChannel}
        isVirtualAccountPayment={isVirtualAccountPayment}
        isVirtualBankTransferConfirmationPending={
          isVirtualBankTransferConfirmationPending
        }
        closeVirtualBankAccountsView={closeVirtualBankAccountsView}
        setBankTransferCompleted={() =>
          setVirtualBankTransferConfirmationPending(true)
        }
        gatewayReservedVirtualAccounts={
          initTxForGatewayApiResponse?.data.gateway_reserved_virtual_accounts
        }
        handleSubmit={onFormSubmit}
        register={register}
        errors={errors}
        control={control}
      />
    </FormProvider>
  );
});
